import { serverCall } from 'utils/serverCall';
import apiEndpoints from 'utils/apiList';
import { initDailyReportRequest, setDailyReport, errorDailyReport, initYearlyReportRequest, setYearlyReport, errorYearlyReport } from './action';
import toast from 'react-hot-toast';

// Daily oil consumption report now expects GET with query params instead of POST body
export const fetchDailyOilConsumptionReport = ({ year, month, machine_ids, oil_type, company_id }) => {
    return async (dispatch) => {
        dispatch(initDailyReportRequest());
        try {
            // Build query manually so commas between machine IDs are not encoded as %2C
            const queryParts = [
                `year=${encodeURIComponent(year)}`,
                `month=${encodeURIComponent(month)}`,
                // leave commas unescaped
                `machine_ids=${machine_ids.join(',')}`,
                `oil_type=${encodeURIComponent(oil_type)}`,
                `company_id=${encodeURIComponent(company_id)}`
            ];
            const url = `${apiEndpoints.REPORT_DAILY_OIL_CONSUMPTION}?${queryParts.join('&')}`;
            const result = await serverCall(url, 'GET', dispatch);
            if (result.success) {
                dispatch(setDailyReport(result.data));
            } else {
                toast.error(result.message || 'Failed to fetch report');
                dispatch(errorDailyReport(result.message));
            }
        } catch (error) {
            toast.error(error.message || 'Failed to fetch report');
            dispatch(errorDailyReport(error.message));
        }
    };
};

// Yearly report now has three modes:
// 1. Machine-wise (existing) -> endpoint: yearly-machinewise-oil-consumption (requires machine_ids + oil_type (except water))
// 2. Oil-wise (new) -> endpoint: yearly-oil-consumption (requires oil_ids). This is used when user selects Oil Wise Monthly view.
//    API sample: /v1/report/yearly-oil-consumption?year=2025&oil_ids=76,77,78&company_id=30
// 3. Monthly Coolant Oil Consumption -> endpoint: machinewise-monthly-coolant-oil-consumption (requires year, machine_ids, company_id)
// Accept optional oil_ids array; when provided (non-empty) we'll call new endpoint; otherwise fallback to machine-wise.
export const fetchYearlyOilConsumptionReport = ({ year, machine_ids = [], oil_type, company_id, oil_ids = [], isMonthlyCoolant = false }) => {
    return async (dispatch) => {
        dispatch(initYearlyReportRequest());
        try {
            let url;
            if (oil_ids && oil_ids.length > 0) {
                console.log('Building oil-wise yearly report URL with oil_ids:', oil_ids);
                const queryParts = [
                    `year=${encodeURIComponent(year)}`,
                    `oil_ids=${oil_ids.join(',')}`,
                    `company_id=${encodeURIComponent(company_id)}`
                ];
                url = `${apiEndpoints.REPORT_YEARLY_OIL_CONSUMPTION}?${queryParts.join('&')}`;
            } else if (isMonthlyCoolant) {
                console.log('Building monthly coolant oil consumption report URL with machine_ids:', machine_ids);
                const queryParts = [
                    `year=${encodeURIComponent(year)}`,
                    `machine_ids=${machine_ids.join(',')}`,
                    `company_id=${encodeURIComponent(company_id)}`
                ];
                url = `${apiEndpoints.REPORT_MACHINEWISE_MONTHLY_COOLANT_OIL}?${queryParts.join('&')}`;
            } else {
                console.log('Building machine-wise yearly report URL with machine_ids:', machine_ids);
                let queryParts, endpoint;

                // Use the same endpoint for all yearly machine-wise reports
                queryParts = [
                    `year=${encodeURIComponent(year)}`,
                    `machine_ids=${machine_ids.join(',')}`,
                    `oil_type=${encodeURIComponent(oil_type)}`,
                    `company_id=${encodeURIComponent(company_id)}`
                ];
                endpoint = apiEndpoints.REPORT_YEARLY_WATER_CONSUMPTION;

                url = `${endpoint}?${queryParts.join('&')}`;
            }
            console.log('Making API call to:', url);
            const result = await serverCall(url, 'GET', dispatch);
            console.log('API response:', result);
            if (result.success) {
                dispatch(setYearlyReport(result.data));
            } else {
                toast.error(result.message || 'Failed to fetch yearly report');
                dispatch(errorYearlyReport(result.message));
            }
        } catch (error) {
            console.error('Error in fetchYearlyOilConsumptionReport:', error);
            toast.error(error.message || 'Failed to fetch yearly report');
            dispatch(errorYearlyReport(error.message));
        }
    };
};

export const fetchDailyCoolantPhReport = ({ year, month, machine_ids, company_id, ri }) => {
    return async (dispatch) => {
        dispatch(initDailyReportRequest());
        try {
            const queryParts = [
                `year=${encodeURIComponent(year)}`,
                `month=${encodeURIComponent(month)}`,
                `machine_ids=${machine_ids.join(',')}`,
                `company_id=${encodeURIComponent(company_id)}`
            ];
            // Only send ri query param when true (as per requirement "if i toggle this then it will send true value")
            if (ri) {
                queryParts.push('ri=true');
            }
            const url = `${apiEndpoints.REPORT_DAILY_COOLANT_PH}?${queryParts.join('&')}`;
            const result = await serverCall(url, 'GET', dispatch);
            if (result.success) {
                // backend returns payload inside data.payload based on sample
                const payload = result.data?.payload || result.data?.data || [];
                // Keep state shape consistent with other daily reports: { data: [...] }
                dispatch(setDailyReport({ data: payload }));
            } else {
                toast.error(result.message || 'Failed to fetch coolant pH report');
                dispatch(errorDailyReport(result.message));
            }
        } catch (error) {
            toast.error(error.message || 'Failed to fetch coolant pH report');
            dispatch(errorDailyReport(error.message));
        }
    };
};
